import { useMemo } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';

import { useAuthContext } from '@myphx-engineering/auth/client';
import { AppShell, useNavBar } from '@myphx-engineering/phx-design-system';
import { MaintenanceBanner, SystemDegradationBanner } from '@phx/maintenance';
import { PwaOverlays } from '@phx/pwa';

import { useAppConfig } from '../../contexts/AppConfigProvider';
import { useSidebarContext } from '../../contexts/SidebarProvider';
import { useAppMaintenanceStatus } from '../../hooks/use-app-maintenance-status';
import { useAppSystemDegradationStatus } from '../../hooks/use-app-system-degradation-status';
import { useOptionalPatientContext } from '../../hooks/use-patient-context';
import { getAbsoluteRoute } from '../../util/get-absolute-route';
import { ConsentWrapper } from '../consents/ConsentWrapper';

import { PrescryptivePowered } from './PrescryptivePowered';
import { SignUpButton } from './SignUpButton';

export const AppContent = () => {
    const { navLogoUrl } = useAppConfig();
    const { isAuthenticated } = useAuthContext();
    const { toggle: toggleSidebar } = useSidebarContext();
    const { backButtonDisabled, backOverrideState } = useNavBar();
    const navigate = useNavigate();
    const { pathname, key } = useLocation();
    const maintenanceStatus = useAppMaintenanceStatus();
    const systemDegradationStatus = useAppSystemDegradationStatus();
    const patientCtx = useOptionalPatientContext();
    const patient = patientCtx?.patient;

    const homeUrl = getAbsoluteRoute('home');
    const isHome = pathname === homeUrl;

    const goHome = useMemo(() => {
        if (isHome) return;

        return () => navigate(homeUrl);
    }, [homeUrl, isHome, navigate]);

    const handleBack = useMemo(() => {
        // Explanation here https://github.com/remix-run/history/blob/main/docs/api-reference.md#locationkey
        const isInitialLocation = key === 'default';

        if (backOverrideState.overrideFn) return backOverrideState.overrideFn;
        if (isHome || isInitialLocation) return;

        return () => {
            const historyIndex = window.history.state?.idx ?? 0;
            if (historyIndex > 0) {
                navigate(-1);
            } else {
                navigate(homeUrl);
            }
        };
    }, [key, isHome, backOverrideState.overrideFn, homeUrl, navigate]);

    const isUnauthHome =
        pathname === getAbsoluteRoute('root') && !isAuthenticated;
    const AvatarAdornment = isUnauthHome ? SignUpButton : undefined;

    return (
        <AppShell
            navBarProps={{
                logoUrl: navLogoUrl,
                logoHeight: '35px',
                logoWidth: 'auto',
                auth: isAuthenticated,
                username: patient?.fullName,
                onAvatarClick: toggleSidebar,
                onBackButtonClick: handleBack,
                hideBackButton: backButtonDisabled,
                onLogoClick: goHome,
                AvatarAdornment,
            }}
            header={
                <>
                    <MaintenanceBanner maintenanceStatus={maintenanceStatus} />
                    <SystemDegradationBanner
                        systemDegradationStatus={systemDegradationStatus}
                    />
                </>
            }
            footer={<PrescryptivePowered isUnauthHome={isUnauthHome} />}
        >
            <ConsentWrapper>
                <Outlet />
            </ConsentWrapper>
            <PwaOverlays />
        </AppShell>
    );
};
